import { Icon as DSIcon } from "@mileiq/design-system";
import format from "date-fns/format";
import isThisYear from "date-fns/isThisYear";
import React, { useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import { CSSTransition, TransitionGroup } from "react-transition-group";

import TeamContext from "src/components/context/TeamContext";
import {
  COLUMNS,
  RECORDS_PER_PAGE,
  useTeamDrives,
} from "src/components/context/TeamDrivesContext";

import MiqButton from "src/components/elements/Button";
import MiqCheckbox from "src/components/elements/Checkbox";
import Dropdown, { MiqDropdownItem } from "src/components/elements/Dropdown";
import Icon from "src/components/elements/Icon";
import Pagination from "src/components/elements/Pagination";
import Tooltip from "src/components/elements/Tooltip";

import TeamMemberName from "src/components/blocks/reports/teams/TeamMemberName";

import useQueryParams from "src/hooks/useQueryParams";
import { DUNNING_STATUS, useTeamsCTA } from "src/hooks/useTeamCTA";

import { SORT_ORDER } from "src/services/http";
import {
  DRIVE_REVIEW_STATUS,
  DRIVE_REVIEW_STATUS_ACTION,
} from "src/services/teams";
import { WebPages, trackTeamsDriveSelected } from "src/services/tracking";
import { isValidDate } from "src/services/utils";

import SleepingCar from "public/assets/img/sleeping-car-transparent.svg";

import DistanceWithCommute from "./DistanceWithCommute";

export default TeamDrivesList;

const COLUMNS_BASE_DATA = {
  [COLUMNS.SELECT]: {
    title: "",
    sortable: false,
    extraClass: "pl-4",
    isToggleAll: true,
    fieldName: "select",
  },
  [COLUMNS.DRIVER]: {
    title: "Driver",
    extraClass: "pl-5 desktop-version",
    sortable: false,
    fieldName: "driver_name",
  },
  [COLUMNS.START_LOCATION]: {
    title: "Start Location",
    extraClass: "desktop-version",
    sortable: false,
    fieldName: "start_location",
  },
  [COLUMNS.END_LOCATION]: {
    title: "End Location",
    extraClass: "desktop-version",
    sortable: false,
    fieldName: "end_location",
  },
  [COLUMNS.MANUALLY_ADDED]: {
    title: "Manual",
    extraClass: "desktop-version",
    sortable: false,
    fieldName: "is_manually_added",
  },
  [COLUMNS.PURPOSE]: {
    title: "Purpose",
    extraClass: "desktop-version",
    sortable: false,
    fieldName: "purpose",
  },
  [COLUMNS.DATE]: {
    title: "Date",
    extraClass: "report-list-column-fix",
    sortable: true,
    fieldName: "end_date",
  },
  [COLUMNS.DISTANCE]: {
    title: "Distance",
    extraClass: "desktop-version",
    sortable: true,
    fieldName: "distance",
  },
  [COLUMNS.VALUE]: {
    title: "Total value",
    sortable: true,
    fieldName: "total_value",
  },
  [COLUMNS.STATUS]: {
    title: "Status",
    sortable: false,
    fieldName: "status",
  },
  [COLUMNS.COMMENTS]: {
    title: "",
    sortable: false,
    fieldName: "comments",
  },
  [COLUMNS.ACTIONS]: {
    title: "",
    sortable: false,
    fieldName: "actions",
    extraClass: "pr-5",
  },
};

function TeamDrivesList() {
  const {
    teamDrives,
    isTeamDrivesLoading,
    hasTeamDrivesLoaded,
    isTeamDrivesEmpty,
    page,
    sortBy,
    order,
    selectedDrive,
    driverId,
    setPastSelectedDriveId,
    areAllDrivesChecked,
    isAnyDriveChecked,
    checkAllDrives,
  } = useTeamDrives();
  const history = useHistory();
  const queryParams = useQueryParams();
  const { team } = useContext(TeamContext);
  const { checkAndHandleDunning } = useTeamsCTA();

  const handleColumnClicked = (columnKey) => {
    if (isTeamDrivesLoading) return;

    let sortOrder = SORT_ORDER.DESC;
    if (sortBy === columnKey) {
      sortOrder = order === SORT_ORDER.DESC ? SORT_ORDER.ASC : SORT_ORDER.DESC;
    }

    queryParams.set("sortBy", columnKey);
    queryParams.set("order", sortOrder);
    queryParams.set("page", 1);
    history.replace({ search: queryParams.toString() });
  };

  const handlePageClicked = (pageIndex) => {
    if (isTeamDrivesLoading) return;

    queryParams.set("page", pageIndex);
    history.replace({ search: queryParams.toString() });
  };

  const handleRowClicked = (driveId) => {
    const dunningStatus = checkAndHandleDunning();
    if (dunningStatus === DUNNING_STATUS.EXPIRED) return;

    const currentDriveId = selectedDrive?.id;
    const isSelected = currentDriveId === driveId;
    const newSelectedDriveId = isSelected ? null : driveId;

    if (selectedDrive) {
      setPastSelectedDriveId(currentDriveId);
    }

    if (newSelectedDriveId) {
      queryParams.set("selectedDriveId", newSelectedDriveId);

      trackTeamsDriveSelected({
        orgId: team.orgId,
        orgGroupId: team.orgGroupId,
        subPlan: team.subscription?.plan,
        webPage: WebPages.DRIVES_LIST_TEAM_DASHBOARD,
      });
    } else {
      queryParams.delete("selectedDriveId");
    }

    history.replace({ search: queryParams.toString() });
  };

  const pagination = {
    pageSize: RECORDS_PER_PAGE,
    currentPage: page,
    total: teamDrives?.total_results || 0,
    onSelectPage: handlePageClicked,
  };

  const hasPagination = teamDrives?.pages > 1;

  let columnsData = COLUMNS_BASE_DATA;

  const isAllDrivers = driverId === "all-drivers";

  if (!isAllDrivers) {
    columnsData = Object.fromEntries(
      Object.entries(columnsData).filter(([key]) => key !== COLUMNS.DRIVER)
    );
  }

  return (
    <div className="team-drives-list flex-grow flex flex-col">
      <div
        className={`miq-list-table team-drives-list-table ${
          isAllDrivers ? "all-drivers" : ""
        }`}
      >
        <div className="miq-list-table-header h-[40px] hover:bg-transparent">
          {Object.keys(columnsData).map((columnKey) => {
            const {
              extraClass,
              title,
              sortable,
              alignRight,
              fieldName,
              isToggleAll,
            } = columnsData[columnKey];

            if (isToggleAll) {
              return (
                <div
                  key={columnKey}
                  className={`select-none cursor-pointer p-2 ${
                    extraClass || ""
                  }`}
                >
                  <MiqCheckbox
                    stopPropagation
                    checked={areAllDrivesChecked}
                    indeterminate={isAnyDriveChecked}
                    onChange={checkAllDrives}
                    disabled={isTeamDrivesLoading || isTeamDrivesEmpty}
                  />
                </div>
              );
            }

            return (
              <div
                key={title || fieldName}
                className={`select-none p-2 ${
                  sortable ? "cursor-pointer" : ""
                } ${extraClass || ""} truncate`}
                onClick={() => sortable && handleColumnClicked(columnKey)}
              >
                <div className={`flex ${alignRight ? "justify-end" : ""}`}>
                  <p className="text-[rgba(23,23,23,0.6)] text-13 truncate">
                    {title}
                  </p>
                  {sortable && sortBy === columnKey ? (
                    <Icon
                      name="sort"
                      color="tertiary"
                      className={`ml-2 transform ${
                        order === SORT_ORDER.DESC ? "rotate-0" : "rotate-180"
                      }`}
                    />
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
        {isTeamDrivesLoading &&
          Array.from({ length: RECORDS_PER_PAGE }).map((_, i) => (
            <TeamDriveRow
              isLoading
              key={`loading-${i}`}
              isAllDrivers={isAllDrivers}
              hasPagination={hasPagination}
            />
          ))}
        {isTeamDrivesEmpty && (
          <div className="w-full min-h-[630px] flex flex-col justify-center items-center gap-[24px]">
            <SleepingCar />
            <p className="text-black/50 text-center max-w-[292px]">
              No drives found matching the selected criteria. Try adjusting your
              filters.
            </p>
          </div>
        )}
        {hasTeamDrivesLoaded && !isTeamDrivesEmpty && (
          <div className="miq-list-table-body">
            <TransitionGroup component="div">
              {teamDrives.data.map((drive) => (
                <CSSTransition
                  key={drive.id}
                  timeout={200}
                  classNames="removable-item"
                >
                  <TeamDriveRow
                    drive={drive}
                    isSelected={selectedDrive?.id === drive.id}
                    hasPagination={hasPagination}
                    isAllDrivers={isAllDrivers}
                    onClick={() => handleRowClicked(drive.id)}
                  />
                </CSSTransition>
              ))}
            </TransitionGroup>
          </div>
        )}
      </div>
      {hasPagination && (
        <div className="my-[15px] mx-5">
          <Pagination {...pagination} />
        </div>
      )}
    </div>
  );
}

const ManuallyAddedIcon = () => {
  const tooltipRef = useRef();

  return (
    <>
      <Icon
        iconRef={tooltipRef}
        className="cursor-pointer"
        name="m-in-circle"
      />
      <Tooltip
        width={122}
        offset={{ y: -45, x: 0 }}
        alignX="center"
        triggerRef={tooltipRef}
        className="py-2.5 px-3"
        timeout={500}
      >
        <p className="text-13">Manually added</p>
      </Tooltip>
    </>
  );
};

const TeamDriveRow = ({
  drive = {},
  isSelected,
  isAllDrivers,
  hasPagination,
  onClick = () => {},
  isLoading,
}) => {
  const { checkedDrives, checkDrive, actionMenuOpen } = useTeamDrives();

  const {
    start_location: startLocation,
    end_location: endLocation,
    started_at: startedAt,
    ended_at: endedAt,
    distance,
    distance_unit: distanceUnit,
    total_value: value,
    currency_unit: currencyUnit,
    is_manually_added: isManuallyAdded,
    user_first_name: driverFirstName,
    user_last_name: driverLastName,
    user_email: driverEmail,
    toll_fees: tolls,
    parking_fees: parking,
    review_status: reviewStatus,
    commute_distance: commuteDistance,
    total_distance: totalDistance,
    purpose,
    has_comment: hasComment,
    automatically_reviewed: automaticallyReviewed,
  } = drive;

  const endDate = new Date(endedAt);
  const startDate = new Date(startedAt);
  const startTime = isValidDate(startDate) && format(startDate, "hh:mm a");
  const endTime = isValidDate(endDate) && format(endDate, "hh:mm a");
  const extras = parseFloat(parking) + parseFloat(tolls);
  const totalValue = parseFloat(value) + extras;

  const driveDate =
    isValidDate(endDate) &&
    format(endDate, isThisYear(endDate) ? "MMM d" : "MMM d, yyyy");

  const shouldRenderMenu = actionMenuOpen === drive.id || isSelected;

  const commentTooltipRef = useRef(null);

  return (
    <div
      role="button"
      tabIndex="0"
      className={`w-full group/row-button text-start miq-list-table-body-row h-[60px] ${
        isSelected ? "bg-beige" : ""
      } ${hasPagination ? "" : "last:border-none last:rounded-b-[14px]"} ${
        isLoading ? "pointer-events-none" : ""
      }`}
      onClick={onClick}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
    >
      {/* Select */}
      <div className="p-4 pr-1">
        {isLoading ? (
          <div className="w-[20px] max-w-full h-[20px] rounded-4 bg-black/10 animate-pulse" />
        ) : (
          <MiqCheckbox
            stopPropagation
            checked={checkedDrives.includes(drive.id)}
            onChange={() => checkDrive({ driveId: drive.id })}
          />
        )}
      </div>

      {/* Driver */}
      {isAllDrivers && (
        <div className="p-2 pl-5 desktop-version">
          {isLoading ? (
            <div className="w-[100px] h-[16px] rounded-4 bg-black/10 animate-pulse" />
          ) : (
            <TeamMemberName
              member={{
                email: driverEmail,
                firstName: driverFirstName,
                lastName: driverLastName,
              }}
            />
          )}
        </div>
      )}

      {/* Start Location */}
      <div className="p-2 truncate">
        {isLoading ? (
          <div className="w-[280px] max-w-full h-[16px] rounded-4 bg-black/10 animate-pulse" />
        ) : (
          <div className="flex flex-col">
            <div className="flex justify-between">
              <span className="text-black truncate">{startLocation}</span>
              <Icon name="arrow-right-3" className="relative -right-3" />
            </div>
            <span className="text-black/70 text-sm">{startTime}</span>
          </div>
        )}
      </div>

      {/* End Location */}
      <div className="p-2 desktop-version truncate">
        {isLoading ? (
          <div className="w-[280px] max-w-full h-[16px] rounded-4 bg-black/10 animate-pulse" />
        ) : (
          <div className="flex flex-col">
            <span className="text-black truncate">{endLocation}</span>
            <span className="text-black/70 text-sm">{endTime}</span>
          </div>
        )}
      </div>

      {/* Is Manually Added */}
      <div className="p-2 desktop-version">
        {isLoading ? (
          <div className="mx-auto w-[24px] max-w-full h-[24px] rounded-4 bg-black/10 animate-pulse" />
        ) : (
          isManuallyAdded && <ManuallyAddedIcon />
        )}
      </div>

      {/* Purpose */}
      <div className="p-2 desktop-version">
        {isLoading ? (
          <div className="w-[140px] max-w-full h-[16px] rounded-4 bg-black/10 animate-pulse" />
        ) : (
          <p className="text-black truncate">{purpose}</p>
        )}
      </div>

      {/* Date */}
      <div className="p-2 desktop-version">
        {isLoading ? (
          <div className="w-[80px] max-w-full h-[16px] rounded-4 bg-black/10 animate-pulse" />
        ) : (
          <p className="text-black">{driveDate}</p>
        )}
      </div>

      {/* Distance */}
      <div className="p-2 report-list-column-fix">
        {isLoading ? (
          <div className="w-[60px] max-w-full h-[16px] rounded-4 bg-black/10 animate-pulse" />
        ) : (
          <DistanceWithCommute
            distanceUnit={distanceUnit}
            totalDistance={totalDistance}
            finalDistance={distance}
            commute={commuteDistance}
            tooltipOffset={{ y: -100 }}
            className="-ml-2"
          >
            <span className="text-black whitespace-nowrap">
              {distance?.toFixed?.(1)} {distanceUnit}
            </span>
          </DistanceWithCommute>
        )}
      </div>

      {/* Total value */}
      <div className="p-2">
        {isLoading ? (
          <div className="w-[50px] max-w-full h-[16px] rounded-4 bg-black/10 animate-pulse" />
        ) : (
          <p className="text-green font-semibold">
            {currencyUnit}
            {totalValue?.toFixed?.(2)}
          </p>
        )}
      </div>

      {/* Status */}
      <div className="p-2">
        {isLoading ? (
          <div className="w-[80px] max-w-full h-[16px] rounded-4 bg-black/10 animate-pulse" />
        ) : (
          <StatusBadge
            reviewStatus={reviewStatus}
            isAutomaticallyReviewed={automaticallyReviewed}
          />
        )}
      </div>

      {/* Comments */}
      <div>
        {hasComment && (
          <>
            <Icon iconRef={commentTooltipRef} name="comment-dots" />
            <Tooltip
              offset={{ y: -50, x: 0 }}
              alignX="center"
              className="py-2.5 px-3"
              timeout={500}
              triggerRef={commentTooltipRef}
            >
              Has comment
            </Tooltip>
          </>
        )}
      </div>

      {/* Actions */}
      <div
        className={`${
          shouldRenderMenu ? "flex" : "hidden"
        } group-focus-visible/row-button:flex group-hover/row-button:flex justify-end gap-3 pr-5 `}
      >
        {!isLoading && (
          <ActionMenu driveId={drive.id} reviewStatus={reviewStatus} />
        )}
      </div>
    </div>
  );
};

const REVIEW_STATUS_BADGE_DATA = {
  [DRIVE_REVIEW_STATUS.ACCEPTED]: {
    bgColor: "bg-[var(--color-surface-positive-light)]",
    textColor: "text-[var(--color-surface-positive)]",
    iconColor: "var(--color-surface-positive)",
    label: "Approved",
  },
  [DRIVE_REVIEW_STATUS.REJECTED]: {
    bgColor: "bg-[var(--color-surface-warning-light)]",
    textColor: "text-[var(--color-yellow-700)]",
    iconColor: "var(--color-yellow-700)",
    label: "Rejected",
  },
};

const StatusBadge = ({ reviewStatus, isAutomaticallyReviewed }) => {
  const badgeRef = useRef(null);

  if (!reviewStatus || reviewStatus === DRIVE_REVIEW_STATUS.PENDING)
    return null;

  const { bgColor, label, textColor, iconColor } = REVIEW_STATUS_BADGE_DATA[
    reviewStatus
  ] || {
    bgColor: "",
    label: "",
    textColor: "",
    iconColor: "",
  };

  return (
    <div
      className={`flex items-center justify-center gap-1 w-fit px-2 py-0.5 rounded-8 h-[30px] ${bgColor}`}
      ref={badgeRef}
      data-chmln={`team-drives-status-badge-${
        isAutomaticallyReviewed ? "automatically" : "manually"
      }-${label.toLowerCase()}`}
    >
      {isAutomaticallyReviewed && (
        <Tooltip triggerRef={badgeRef} alignX="center" offset={{ y: -45 }}>
          <p>Automatically {label.toLowerCase()}</p>
        </Tooltip>
      )}
      <span className={textColor}>{label}</span>
      {isAutomaticallyReviewed && (
        <DSIcon name="zap" color={iconColor} size={16} />
      )}
    </div>
  );
};

const ActionMenu = ({ driveId, reviewStatus }) => {
  const { actionMenuOpen, setActionMenuOpen, reviewDrives } = useTeamDrives();

  const isOpen = actionMenuOpen === driveId;

  const removeStatusAction = ({ isChangingStatus = false }) => ({
    icon: "file-times-alt",
    label: "Remove status",
    handler: () =>
      reviewDrives({
        driveIds: [driveId],
        action: DRIVE_REVIEW_STATUS_ACTION.RESET,
        shouldFetchMore: false,
        shouldUpdateCurrentPage: true,
        isChangingStatus,
      }).finally(() => {
        setActionMenuOpen(false);
      }),
  });

  const approveAction = ({ isChangingStatus = false }) => ({
    icon: "checked-circle-black",
    label: "Change to Approved",
    handler: () =>
      reviewDrives({
        driveIds: [driveId],
        action: DRIVE_REVIEW_STATUS_ACTION.ACCEPT,
        shouldFetchMore: false,
        shouldUpdateCurrentPage: true,
        isChangingStatus,
      }).finally(() => {
        setActionMenuOpen(false);
      }),
  });

  const rejectAction = ({ isChangingStatus = false }) => ({
    icon: "ban",
    label: "Change to Rejected",
    handler: () =>
      reviewDrives({
        driveIds: [driveId],
        action: DRIVE_REVIEW_STATUS_ACTION.REJECT,
        shouldFetchMore: false,
        shouldUpdateCurrentPage: true,
        isChangingStatus,
      }).finally(() => {
        setActionMenuOpen(false);
      }),
  });

  const actions = {
    [DRIVE_REVIEW_STATUS.ACCEPTED]: [
      rejectAction({ isChangingStatus: true }),
      removeStatusAction({ isChangingStatus: true }),
    ],
    [DRIVE_REVIEW_STATUS.REJECTED]: [
      approveAction({ isChangingStatus: true }),
      removeStatusAction({ isChangingStatus: true }),
    ],
    [DRIVE_REVIEW_STATUS.PENDING]: [
      {
        ...approveAction({ isChangingStatus: false }),
        label: "Approve",
      },
      {
        ...rejectAction({ isChangingStatus: false }),
        label: "Reject",
      },
    ],
  };

  return (
    <Dropdown
      className=""
      open={isOpen}
      stopPropagation
      onOpen={() => setActionMenuOpen(driveId)}
      onClose={() => setActionMenuOpen(false)}
      renderTrigger={() => (
        <MiqButton
          ghost
          icon="ellipsis-vert"
          className={
            isOpen
              ? "hover:bg-[rgba(23,23,23,0.12)] bg-[rgba(23,23,23,0.12)]"
              : "hover:bg-[rgba(23,23,23,0.06)]"
          }
        />
      )}
      triggerClassName="shadow-none bg-transparent p-0"
      contentClassName="right-3 top-11 p-2 border border-border-1"
    >
      {actions[reviewStatus]?.map((action) => (
        <MiqDropdownItem
          key={action.label}
          icon={action.icon}
          onClick={(e) => {
            e.stopPropagation();
            action.handler();
          }}
        >
          {action.label}
        </MiqDropdownItem>
      ))}
    </Dropdown>
  );
};
